import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const fetchedRef = useRef(false);

    // Fetch current user info (called only once on mount or when explicitly refreshed)
    const fetchUser = useCallback(async (force = false) => {
        // Prevent duplicate fetches in React Strict Mode unless forced
        if (fetchedRef.current && !force) {
            setLoading(false);
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            setUser(null);
            setLoading(false);
            fetchedRef.current = true;
            return;
        }

        try {
            setLoading(true);
            const response = await api.get('/auth/me');
            setUser(response.data);
            setError(null);
            fetchedRef.current = true;

            // Store user role in localStorage for PrivateRoute
            localStorage.setItem('userRole', response.data.role);
            localStorage.setItem('userLocation', response.data.location || 'Universal');
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setUser(null);
            fetchedRef.current = true;
            // Clear invalid token
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userLocation');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initialize user on mount
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Refresh user data (useful after profile updates)
    const refreshUser = useCallback(() => {
        return fetchUser(true); // Force fetch
    }, [fetchUser]);

    // Clear user data on logout
    const clearUser = useCallback(() => {
        setUser(null);
        setError(null);
        fetchedRef.current = false; // Reset fetched flag
    }, []);

    const value = {
        user,
        loading,
        error,
        refreshUser,
        clearUser,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
